import api from "./base-api"

const normalizeClashCampaign = (campaign) => ({
    ...campaign,
    id: campaign.campaignId,
    previousEventId: campaign.previousCampaignId,
    distribution: {
        start: campaign.startAtMs,
        end: campaign.endAtMs,
    },
    createdAt: campaign.createdAtMs,
});

const normalizeClashCampaignsAround = (response = {}) => ({
    active: (response.active || []).map(normalizeClashCampaign),
    next: (response.next || []).map(normalizeClashCampaign),
    previous: (response.previous || []).map(normalizeClashCampaign),
});

const tohClansApi = api.injectEndpoints({
    endpoints: (build) => ({
        addPlayerToClanTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/add-player?userId=${data.userId}&clanId=${data.clanId}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClanInfo']
            
        }),

        removePlayerFromClanTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/remove-player?userId=${data.userId}&clanId=${data.clanId}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClanInfo']

        }),

        addClanExperienceTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/add-experience?clanId=${data.clanId}&experience=${data.experience}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClanInfo']

        }),

        addPlayerReputationTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/add-reputation?userId=${data.userId}&score=${data.score}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClanInfo']

        }),

        deleteClanTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/delete-clan?clanId=${data.clanId}`,
                method: "DELETE",
            }),
            invalidatesTags: ['TohClanInfo']

        }),

        setClanLeaderTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/set-leader?userId=${data.userId}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClanInfo']

        }),

        renameClanTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/rename?clanId=${data.clanId}&name=${encodeURIComponent(data.name)}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClanInfo']

        }),

        getAroundDepotEventsTOH: build.query({
            query: () => `v2/toh/clans/depot/around`,
            providesTags: ['TohDepotEvents'],
        }),

        getAroundClashCampaignsTOH: build.query({
            query: () => `v2/toh/clans/clash/around`,
            transformResponse: normalizeClashCampaignsAround,
            providesTags: ['TohClashEvents'],
        }),

        getUserClanInfoTOH: build.query({
            query: (userId) => `v2/toh/clans/user-info?userId=${userId}`,
            providesTags: ['TohClanInfo'],
        }),

        searchClanByNameTOH: build.mutation({
            query: (name) => `v2/toh/clans/search-by-name?name=${encodeURIComponent(name)}`,
            providesTags: ['TohClanInfo']
        }),

        getGlobalChatTOH: build.mutation({
            query: (body = {}) => ({
                url: `v2/toh/clans/chat/global`,
                method: "POST",
                body: body,
            }),
        }),

        getClanChatTOH: build.mutation({
            query: (body = {}) => ({
                url: `v2/toh/clans/chat/clan`,
                method: "POST",
                body: body,
            }),
        }),

        mutePlayerTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/mute?userId=${data.userId}&mutedUntilSeconds=${data.mutedUntilSeconds}&reason=${data.reason}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClanInfo'],
        }),

        unmutePlayerTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/unmute?userId=${data.userId}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClanInfo'],
        }),

        addBossAllianceDamageTOH: build.mutation({
            query: (body = {}) => ({
                url: `v2/toh/clans/boss-alliance/add-damage`,
                method: "POST",
                body: body,
            }),
            invalidatesTags: ['TohBossAllianceBoosts', 'TohBossAllianceProgress'],
        }),

        addDepotPointsTOH: build.mutation({
            query: (body = {}) => ({
                url: `v2/toh/clans/depot/add-points`,
                method: "POST",
                body: body,
            }),
            invalidatesTags: ['TohDepotProgress'],
        }),

        addBossAllianceBoostTOH: build.mutation({
            query: (body = {}) => ({
                url: `v2/toh/clans/boss-alliance/add-boost`,
                method: "POST",
                body: body,
            }),
            invalidatesTags: ['TohBossAllianceBoosts'],
        }),

        getAroundBossAllianceEventsTOH: build.query({
            query: () => `v2/toh/clans/boss-alliance/around`,
            providesTags: ['TohBossAllianceEvents'],
        }),

        getBossAllianceBoostsTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/boss-alliance/boosts?eventId=${encodeURIComponent(data.eventId)}&clanId=${encodeURIComponent(data.clanId)}`,
            providesTags: (result, error, arg) => [
                {type: 'TohBossAllianceBoosts', id: `${arg?.eventId}|${arg?.clanId}`},
                'TohBossAllianceBoosts',
            ],
        }),

        getBossAllianceProgressTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/boss-alliance/progress?eventId=${encodeURIComponent(data.eventId)}&clanId=${encodeURIComponent(data.clanId)}`,
            providesTags: (result, error, arg) => [
                {type: 'TohBossAllianceProgress', id: `${arg?.eventId}|${arg?.clanId}`},
                'TohBossAllianceProgress',
            ],
        }),

        getDepotProgressTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/depot/progress?eventId=${encodeURIComponent(data.eventId)}&clanId=${encodeURIComponent(data.clanId)}`,
            providesTags: (result, error, arg) => [
                {type: 'TohDepotProgress', id: `${arg?.eventId}|${arg?.clanId}`},
                'TohDepotProgress',
            ],
        }),

        getClashProgressTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/clash/progress?campaignId=${encodeURIComponent(data.eventId)}&clanId=${encodeURIComponent(data.clanId)}`,
            transformResponse: (response) => ({
                ...response,
                lastPointsAt: response.lastPointsAtMs,
            }),
            providesTags: (result, error, arg) => [
                {type: 'TohClashProgress', id: `${arg?.eventId}|${arg?.clanId}`},
                'TohClashProgress',
            ],
        }),

        getBossLeaguesTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/boss-alliance/leagues?eventId=${encodeURIComponent(data.eventId)}`,
            providesTags: (result, error, arg) => [
                {type: 'TohBossLeagues', id: arg?.eventId},
                'TohBossLeagues',
            ],
        }),

        getBossLeagueTopTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/boss-alliance/leagues/${encodeURIComponent(data.league)}/top?eventId=${encodeURIComponent(data.eventId)}`,
            providesTags: (result, error, arg) => [
                {type: 'TohBossLeagueTop', id: `${arg?.eventId}|${arg?.league}`},
                'TohBossLeagueTop',
            ],
        }),

        getDepotLeaguesTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/depot/leagues?eventId=${encodeURIComponent(data.eventId)}`,
            providesTags: (result, error, arg) => [
                {type: 'TohDepotLeagues', id: arg?.eventId},
                'TohDepotLeagues',
            ],
        }),

        getDepotLeagueTopTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/depot/leagues/${encodeURIComponent(data.league)}/top?eventId=${encodeURIComponent(data.eventId)}`,
            providesTags: (result, error, arg) => [
                {type: 'TohDepotLeagueTop', id: `${arg?.eventId}|${arg?.league}`},
                'TohDepotLeagueTop',
            ],
        }),

        getClashLeaguesTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/clash/leagues?campaignId=${encodeURIComponent(data.eventId)}`,
            providesTags: (result, error, arg) => [
                {type: 'TohClashLeagues', id: arg?.eventId},
                'TohClashLeagues',
            ],
        }),

        getClashLeagueTopTOH: build.query({
            query: (data = {}) =>
                `v2/toh/clans/clash/leagues/${encodeURIComponent(data.league)}/top?campaignId=${encodeURIComponent(data.eventId)}`,
            providesTags: (result, error, arg) => [
                {type: 'TohClashLeagueTop', id: `${arg?.eventId}|${arg?.league}`},
                'TohClashLeagueTop',
            ],
        }),

        awardBossLeagueTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/boss-alliance/leagues/${encodeURIComponent(data.league)}/award?eventId=${encodeURIComponent(data.eventId)}`,
                method: "POST",
            }),
            invalidatesTags: ['TohBossLeagueTop', 'TohBossLeagues', 'TohBossAllianceProgress'],
        }),

        awardDepotLeagueTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/depot/leagues/${encodeURIComponent(data.league)}/award?eventId=${encodeURIComponent(data.eventId)}`,
                method: "POST",
            }),
            invalidatesTags: ['TohDepotLeagueTop', 'TohDepotLeagues', 'TohDepotProgress'],
        }),

        awardClashLeagueTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/clash/leagues/${encodeURIComponent(data.league)}/award?campaignId=${encodeURIComponent(data.eventId)}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClashLeagueTop', 'TohClashLeagues', 'TohClashProgress'],
        }),

        setBossLeagueScoresTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/boss-alliance/leagues/${encodeURIComponent(data.league)}/set-scores`,
                method: "POST",
                body: {eventId: data.eventId, scores: data.scores},
            }),
            invalidatesTags: ['TohBossLeagueTop', 'TohBossLeagues', 'TohBossAllianceProgress'],
        }),

        setDepotLeagueScoresTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/depot/leagues/${encodeURIComponent(data.league)}/set-scores`,
                method: "POST",
                body: {eventId: data.eventId, scores: data.scores},
            }),
            invalidatesTags: ['TohDepotLeagueTop', 'TohDepotLeagues', 'TohDepotProgress'],
        }),

        setClashLeagueScoresTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/clash/leagues/${encodeURIComponent(data.league)}/set-scores`,
                method: "POST",
                body: {campaignId: data.eventId, scores: data.scores},
            }),
            invalidatesTags: ['TohClashLeagueTop', 'TohClashLeagues', 'TohClashProgress'],
        }),

        getBossLeagueRecalculationStatusTOH: build.query({
            query: (data = {}) => {
                const params = new URLSearchParams({eventId: data.eventId});
                if (data.runId) params.set('runId', data.runId);
                return `v2/toh/clans/boss-alliance/leagues/prepare/status?${params.toString()}`;
            },
        }),

        scheduleBossLeagueRecalculationTOH: build.mutation({
            query: (data = {}) => {
                const params = new URLSearchParams({eventId: data.eventId});
                if (data.prevEventId) params.set('prevEventId', data.prevEventId);
                return {
                    url: `v2/toh/clans/boss-alliance/leagues/prepare?${params.toString()}`,
                    method: "POST",
                };
            },
        }),

        getDepotLeagueRecalculationStatusTOH: build.query({
            query: (data = {}) => {
                const params = new URLSearchParams({eventId: data.eventId});
                if (data.runId) params.set('runId', data.runId);
                return `v2/toh/clans/depot/leagues/prepare/status?${params.toString()}`;
            },
        }),

        scheduleDepotLeagueRecalculationTOH: build.mutation({
            query: (data = {}) => {
                const params = new URLSearchParams({eventId: data.eventId});
                if (data.prevEventId) params.set('prevEventId', data.prevEventId);
                return {
                    url: `v2/toh/clans/depot/leagues/prepare?${params.toString()}`,
                    method: "POST",
                };
            },
        }),

        getClashLeagueRecalculationStatusTOH: build.query({
            query: (data = {}) => {
                const params = new URLSearchParams({eventId: data.eventId});
                if (data.runId) params.set('runId', data.runId);
                return `v2/toh/clans/clash/leagues/prepare/status?${params.toString()}`;
            },
        }),

        scheduleClashLeagueRecalculationTOH: build.mutation({
            query: (data = {}) => {
                const params = new URLSearchParams({eventId: data.eventId});
                if (data.prevEventId) params.set('prevEventId', data.prevEventId);
                return {
                    url: `v2/toh/clans/clash/leagues/prepare?${params.toString()}`,
                    method: "POST",
                };
            },
        }),

        clearBossAllianceProgressTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/boss-alliance/progress/clear?eventId=${encodeURIComponent(data.eventId)}&clanId=${encodeURIComponent(data.clanId)}`,
                method: "POST",
            }),
            invalidatesTags: ['TohBossAllianceBoosts', 'TohBossAllianceProgress'],
        }),

        clearAllBossAllianceProgressTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/boss-alliance/progress/clear-all?eventId=${encodeURIComponent(data.eventId)}`,
                method: "POST",
            }),
            invalidatesTags: [
                'TohBossAllianceBoosts',
                'TohBossAllianceProgress',
                'TohBossLeagues',
                'TohBossLeagueTop',
            ],
        }),

        clearDepotProgressTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/depot/progress/clear?eventId=${encodeURIComponent(data.eventId)}&clanId=${encodeURIComponent(data.clanId)}`,
                method: "POST",
            }),
            invalidatesTags: ['TohDepotEvents', 'TohDepotProgress'],
        }),

        clearAllDepotProgressTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/depot/progress/clear-all?eventId=${encodeURIComponent(data.eventId)}`,
                method: "POST",
            }),
            invalidatesTags: [
                'TohDepotEvents',
                'TohDepotProgress',
                'TohDepotLeagues',
                'TohDepotLeagueTop',
            ],
        }),

        clearClashProgressTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/clash/progress/clear?campaignId=${encodeURIComponent(data.eventId)}&clanId=${encodeURIComponent(data.clanId)}`,
                method: "POST",
            }),
            invalidatesTags: ['TohClashEvents', 'TohClashProgress'],
        }),

        clearAllClashProgressTOH: build.mutation({
            query: (data = {}) => ({
                url: `v2/toh/clans/clash/progress/clear-all?campaignId=${encodeURIComponent(data.eventId)}`,
                method: "POST",
            }),
            invalidatesTags: [
                'TohClashEvents',
                'TohClashProgress',
                'TohClashLeagues',
                'TohClashLeagueTop',
            ],
        }),

        getResearchDonationsTOH: build.query({
            query: (data = {}) => {
                const params = new URLSearchParams({ clanId: data.clanId });
                if (data.userId) params.set('userId', data.userId);
                return `v2/toh/clans/research/donations?${params.toString()}`;
            },
        }),

        getClanEventsTOH: build.query({
            query: (data = {}) => {
                const params = new URLSearchParams({
                    page: String(data.page ?? 0),
                    limit: String(data.limit ?? 100),
                });
                (data.actions || []).forEach((a) => params.append('actions', a));
                if (data.userId) params.set('userId', data.userId);
                if (data.expelledUserId) params.set('expelledUserId', data.expelledUserId);
                return `v2/toh/clans/${encodeURIComponent(data.allianceId)}/events?${params.toString()}`;
            },
        }),
    }),
    overrideExisting: false,
})

export const {
    useAddPlayerToClanTOHMutation,
    useRemovePlayerFromClanTOHMutation,
    useAddClanExperienceTOHMutation,
    useAddPlayerReputationTOHMutation,
    useDeleteClanTOHMutation,
    useSetClanLeaderTOHMutation,
    useRenameClanTOHMutation,
    useGetAroundDepotEventsTOHQuery,
    useGetAroundClashCampaignsTOHQuery,
    useGetUserClanInfoTOHQuery,
    useSearchClanByNameTOHMutation,
    useGetGlobalChatTOHMutation,
    useGetClanChatTOHMutation,
    useMutePlayerTOHMutation,
    useUnmutePlayerTOHMutation,
    useAddBossAllianceDamageTOHMutation,
    useAddDepotPointsTOHMutation,
    useAddBossAllianceBoostTOHMutation,
    useGetAroundBossAllianceEventsTOHQuery,
    useGetBossAllianceBoostsTOHQuery,
    useGetBossAllianceProgressTOHQuery,
    useGetDepotProgressTOHQuery,
    useGetClashProgressTOHQuery,
    useGetBossLeaguesTOHQuery,
    useGetBossLeagueTopTOHQuery,
    useGetDepotLeaguesTOHQuery,
    useGetDepotLeagueTopTOHQuery,
    useGetClashLeaguesTOHQuery,
    useGetClashLeagueTopTOHQuery,
    useAwardBossLeagueTOHMutation,
    useAwardDepotLeagueTOHMutation,
    useAwardClashLeagueTOHMutation,
    useSetBossLeagueScoresTOHMutation,
    useSetDepotLeagueScoresTOHMutation,
    useSetClashLeagueScoresTOHMutation,
    useGetBossLeagueRecalculationStatusTOHQuery,
    useScheduleBossLeagueRecalculationTOHMutation,
    useGetDepotLeagueRecalculationStatusTOHQuery,
    useScheduleDepotLeagueRecalculationTOHMutation,
    useGetClashLeagueRecalculationStatusTOHQuery,
    useScheduleClashLeagueRecalculationTOHMutation,
    useClearBossAllianceProgressTOHMutation,
    useClearAllBossAllianceProgressTOHMutation,
    useClearDepotProgressTOHMutation,
    useClearAllDepotProgressTOHMutation,
    useClearClashProgressTOHMutation,
    useClearAllClashProgressTOHMutation,
    useGetResearchDonationsTOHQuery,
    useGetClanEventsTOHQuery,
} = tohClansApi
